import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { RouterLink } from '@angular/router';

export interface UiBreadcrumbItem {
  id: string;
  label: string;
  /** Ruta interna Angular (routerLink) */
  routerLink?: string | any[];
  /** URL externa */
  href?: string;
  /** Icono SVG/PNG (src) — útil para el ítem home */
  iconSrc?: string;
  disabled?: boolean;
}

/**
 * Átomo: breadcrumb de navegación jerárquica.
 * Alineado con PrimeReact Breadcrumb: ítem `home` opcional + lista de `items`.
 * El último ítem siempre es no clickeable (`aria-current="page"`).
 * Sin lógica de negocio ni Router inyectado; navegación vía `routerLink` / `href`.
 */
@Component({
  selector: 'ui-breadcrumb',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss',
  host: {
    class: 'ui-breadcrumb-host',
  },
})
export class UiBreadcrumbComponent {
  /** Ítem inicial (ícono de casa). Se muestra antes de `items`. */
  readonly home = input<UiBreadcrumbItem | null>(null);

  /** Lista de ítems de ruta. El último se marca como página actual. */
  readonly items = input<readonly UiBreadcrumbItem[]>([]);

  /** Etiqueta accesible del `<nav>` */
  readonly ariaLabel = input('Ruta de navegación');

  /** Emite el ítem al hacer click (excepto en el último) */
  readonly itemSelect = output<UiBreadcrumbItem>();

  /** Todos los ítems combinados (home + items) para el template */
  protected readonly allItems = computed<readonly UiBreadcrumbItem[]>(() => {
    const h = this.home();
    return h ? [h, ...this.items()] : [...this.items()];
  });

  protected isLast(index: number): boolean {
    return index === this.allItems().length - 1;
  }

  protected onItemClick(item: UiBreadcrumbItem, event: Event): void {
    if (item.disabled) {
      event.preventDefault();
      return;
    }
    this.itemSelect.emit(item);
  }
}
